import {FC, useCallback, useState} from 'react';
import {styled} from 'styled-components';
import {useLocation} from 'wouter';

import {PreciseTimestamp} from '@shared/models/core_models';

import {Button} from '@shared_web/components/core/button';
import {showModal} from '@shared_web/components/core/modal';
import {showAlert, showSuccess} from '@shared_web/components/core/notifications';
import {StyledTextarea} from '@shared_web/components/core/textarea';

import {PageWrapper} from '@mavismart_frontend/components/common_styles';
import {GenericConfirmModal} from '@mavismart_frontend/components/generic_confirm_modal';
import {FileInput, Media} from '@mavismart_frontend/components/input_file';
import {translate, Translate} from '@mavismart_frontend/components/translate';
import {apiCall} from '@mavismart_frontend/lib/api';
import {BUTTON_THEME, MODAL_THEME, MODAL_TITLE_STYLE} from '@mavismart_frontend/lib/constants';
import {getUserLanguage, useAccount} from '@mavismart_frontend/stores/account_store';

interface SendToMaviflexProps {
  doorId: string;
  doorMessageTs: PreciseTimestamp;
  category: string;
}

export const SendToMaviflex: FC<SendToMaviflexProps> = ({doorId, doorMessageTs, category}) => {
  const account = useAccount();

  const [comments, setComments] = useState<string | undefined>();
  const [medias, setMedias] = useState<Media[] | undefined>(undefined);
  const [, navigate] = useLocation();

  const onMediaUploaded = useCallback((media: Media) => {
    setMedias(prev => [...(prev ?? []), media]);
  }, []);

  const onMediaDeleted = useCallback((mediaId: string) => {
    setMedias(prev => prev?.filter(media => media.mediaId !== mediaId));
  }, []);

  const handleCommentsChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComments(e.target.value);
  }, []);

  const handleSendEmail = useCallback(async () => {
    try {
      if (!comments) {
        showAlert(translate('please_add_comments', getUserLanguage(account)));
        return;
      }
      await apiCall('POST /create-support-message', {
        doorId,
        doorMessageTs,
        content: comments,
        mediaIds: (medias ?? []).map(m => m.mediaId),
      });
      setComments(undefined);
      setMedias(undefined);
      showSuccess(translate('email_sent_successfully', getUserLanguage(account)));
      navigate(`/doors/${doorId}/messages/${category}`);
    } catch {
      throw new Error(translate('failed_to_send_email', getUserLanguage(account)));
    }
  }, [doorId, doorMessageTs, comments, medias, account, navigate, category]);

  const handleSend = useCallback(() => {
    showModal({
      title: (
        <MODAL_TITLE_STYLE>
          <Translate value="send_to_Maviflex" />
        </MODAL_TITLE_STYLE>
      ),
      ...MODAL_THEME,
      children: (
        <GenericConfirmModal
          onConfirm={handleSendEmail}
          sendData
          subtitle={<Translate value="want_to_send_email" />}
        />
      ),
    });
  }, [handleSendEmail]);

  return (
    <PageWrapper>
      <Top>
        <Title>
          <Translate value="share_incident" />
        </Title>
        <StyledTextarea
          placeholder={translate('add_your_comment_here', getUserLanguage(account))}
          rows={4}
          $resize="vertical"
          defaultValue={comments}
          onChange={handleCommentsChange}
        />
        <FileInput
          doorId={doorId}
          onMediaUploaded={onMediaUploaded}
          onMediaDeleted={onMediaDeleted}
        />
      </Top>
      <WrapperButtons>
        <Button onClick={handleSend} themeOverrides={BUTTON_THEME}>
          <Translate value="send_to_Maviflex" />
        </Button>
      </WrapperButtons>
    </PageWrapper>
  );
};

SendToMaviflex.displayName = 'SendToMaviflex';

const Title = styled.div`
  font-size: 20px;
  margin-bottom: 10px;
`;
const Top = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const WrapperButtons = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 10px;
`;
